import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter, useHistory, matchPath } from 'react-router-dom';
import {
    cloneDeep, get as _get, includes, isEmpty, set,
} from 'lodash';

import URLSearchParams from '@ungap/url-search-params';
import { REGULAR } from '../constants/UserType';
import { EMPLOYER_RER_ADMIN_ROLE } from '../constants/UserRole';
import AuthenticationHttpService from '../api/AuthenticationHttpService';
import BackendConnector from '../api/backendConnector';
import Logger from '../services/logger';
import ImpersonationService from '../services/ImpersonationService';
import PermissionService from '../services/PermissionService';
import PublicContext from './PublicContext';
import TrackingContext from './TrackingContext';
import SettingsContext from './SettingsContext';
import EmployerRepHttpService from '../api/EmployerRepHttpService';
import ChromeExtensionService from '../services/ChromeExtensionService';
import EmployerHttpService from '../api/EmployerHttpService';
import LocalStorageService from '../services/LocalStorageService';
import settings from '../../config/settings';
import { EMPLOYER_REP_STATUSES } from '../constants/EmployerRep';
import UtilsService from '../services/UtilsService';
import { EmployerRepAuthTypes } from '../constants/employerRepaAuthTypes';
import EmployerRepService from '../services/EmployerRepService';
import TextService from '../services/TextService';
import NotificationContext from './NotificationContext';

const AuthorizationContext = React.createContext({
    user: null,
    logout: () => {},
    tryToAuthorize: () => {},
    tryToAuthorizeSSO: () => {},
});

export const AuthorizationProvider = AuthorizationContext.Provider;

export const AuthorizationConsumer = AuthorizationContext.Consumer;

export const AuthEntityTypes = {
    EMPLOYER_REP: 'employerRep',
    RECRUITER: 'recruiter',
};

const AuthorizationComponent = ({
    children,
    location,
}) => {
    const isPublicMode = useContext(PublicContext);
    const { initUserTracking } = useContext(TrackingContext);
    const { environmentSettings: { TFTAppUrl, BackendUrl } } = useContext(SettingsContext);
    const { showNotification } = useContext(NotificationContext);
    const { fetchSystemConfigAndPreferences, onAuthCompleteCallback, systemPreferences } = useContext(SettingsContext);

    const ssoLoginSessionExpirationTimeSec = _get(systemPreferences, 'ssoLoginSessionExpirationTimeSec', 60 * 10);
    const isOnlyWebAppLoginAllowed = _get(systemPreferences, 'isOnlyWebAppLoginAllowed', true);
    const usersAllowedToEditSystemCampaigns = _get(systemPreferences, 'usersAllowedToEditSystemCampaigns', []);

    const [authToken, setAuthToken] = useState();
    const [authorizedUser, setUser] = useState(null);
    const [employerRep, setEmployerRep] = useState(null);
    const [availableEmployerIds, setAvailableEmployerIds] = useState([]);

    const [authorizationProcess, setAuthorizationProcessState] = useState(!isPublicMode);
    const [userLinkedinId, setUserLinkedinId] = useState();

    const history = useHistory();

    let syncLinkedinConnectionsTimer = 0;
    const syncLinkedinConnectionsTimeout = 3600000;

    const setBehalfOfHeaders = useCallback((impersonation) => {
        if (impersonation.impersonatedAsRecruiter) {
            return BackendConnector.addHeaders({
                'on-behalf': JSON.stringify(
                    { recruiterId: impersonation.impersonatedEntityId },
                ),
            });
        }

        if (impersonation.impersonatedAsEmployer) {
            return BackendConnector.addHeaders({
                'on-behalf': JSON.stringify(
                    { employerId: impersonation.impersonatedEntityId },
                ),
            });
        }

        return null;
    }, []);

    const setAuthorizedUser = useCallback(({
        user,
        userPrefs,
        origUserPrefs,
        hasEmployerRepAdminRole,
    }) => {
        if (user) {
            const impersonation = ImpersonationService.getImpersonation(
                user,
                userPrefs,
                origUserPrefs,
                location,
            );

            const permissions = PermissionService.getPermissions(
                user,
                hasEmployerRepAdminRole,
            );

            setBehalfOfHeaders(impersonation);

            const newAuthorizedUser = {
                ...user,
                impersonation,
                permissions,
                userPrefs,
                origUserPrefs,
            };

            setUser(newAuthorizedUser);
            initUserTracking(newAuthorizedUser, (user.roles || []).includes('TALENYA'));
        } else {
            setUser(user);
        }

        onAuthCompleteCallback();
    }, [
        location,
        setBehalfOfHeaders,
        initUserTracking,
    ]);

    const isTalenyaUser = useMemo(
        () => authorizedUser
            && _get(authorizedUser, 'permissions.isTalenyaUser'),
        [authorizedUser],
    );

    const isTalentExpertUser = useMemo(
        () => authorizedUser
            && _get(authorizedUser, 'permissions.isTalentExpertUser'),
        [authorizedUser],
    );

    const isEmployerRepAdmin = useMemo(
        () => authorizedUser
            && _get(authorizedUser, 'permissions.isEmployerRepAdmin'),
        [authorizedUser],
    );

    const isEmployerUser = useMemo(
        () => authorizedUser
            && _get(authorizedUser, 'permissions.isEmployerUser'),
        [authorizedUser],
    );

    const isUserAllowedToEditSystemCampaigns = useMemo(
        () => {
            const email = _get(authorizedUser, 'email', null);
            return email && includes(usersAllowedToEditSystemCampaigns, email);
        },
        [authorizedUser, usersAllowedToEditSystemCampaigns],
    );

    const updateImpersonationInfo = () => {
        if (authorizedUser) {
            const impersonation = ImpersonationService.getImpersonation(
                authorizedUser,
                authorizedUser.userPrefs,
                authorizedUser.origUserPrefs,
                location,
            );

            setBehalfOfHeaders(impersonation);

            setUser({
                ...authorizedUser,
                impersonation,
            });
        }
    };

    const loadInBackground = async (url, callback, remove = true) => {
        const iframe = document.createElement('iframe');
        iframe.style = 'width: 1px; height: 1px; display: hidden; position: absolute; top: -1000px; left: -1000px;';
        iframe.onload = async () => {
            if (remove) {
                document.body.removeChild(iframe);
            }
            callback(remove ? null : iframe);
        };
        iframe.src = url;
        document.body.appendChild(iframe);
        return iframe;
    };

    const checkIfThirdPartyCookieEnabled = async () => {
        if (includes(BackendUrl, 'localhost')) {
            return;
        }
        const url = `${BackendUrl}/user/thirdPartyCookieTest`;
        const iframe = await loadInBackground(url, (iframeEl) => {
            iframeEl.contentWindow.postMessage('getCookie', BackendUrl);
        }, false);

        let cookieChecked = false;
        setTimeout(() => {
            if (!cookieChecked) {
                if (iframe && iframe?.parentNode) {
                    document.body.removeChild(iframe);
                }
                window.removeEventListener('message', messageHandler, false);
            }
        }, 20000);
        const messageHandler = (event) => {
            try {
                if (event.origin !== BackendUrl) {
                    return;
                }
                if (includes(event.data, 'thirdPartyCookieTest=1')) {
                    // Third-party cookies are enabled
                    Logger.info('Third-party cookies are enabled');
                } else {
                    // Third-party cookies are blocked
                    showNotification({
                        message: 'It looks like third-party cookies are blocked. Application may not work properly. Please enable third-party cookies in browser security settings.',
                        type: 'error',
                        time: 15000,
                    });
                    Logger.error('Third-party cookies are blocked');
                }
                if (iframe && iframe?.parentNode) {
                    document.body.removeChild(iframe);
                }
                cookieChecked = true;
                window.removeEventListener('message', messageHandler, false);
            } catch (e) {
                Logger.error(`Some error occured: ${e?.message}`);
            }
        };

        window.addEventListener('message', messageHandler, false);
    };

    const tryToAuthorizeSSO = useCallback(
        async ({ authData }) => {
            setAuthorizationProcessState(true);

            try {
                const idToken = await AuthenticationHttpService.getIdToken();
                const accessToken = AuthenticationHttpService.getSsoHashAccessToken();

                if (!idToken && !accessToken) {
                    const loginHint = authData?.data?.loginHint;
                    const connection = authData?.data?.connection;
                    const redirectTo = authData?.data?.redirectTo;
                    AuthenticationHttpService.getAuth0(authData).authorize({
                        ...(loginHint && { login_hint: loginHint }),
                        ...(connection && { connection }),
                        appState: {
                            ...(redirectTo && { redirectTo }),
                        },
                    });
                    return;
                }

                let token = idToken;

                let redirectTo = '';
                if (!token) {
                    try {
                        const authResult = await AuthenticationHttpService.getSSOAuthResult({ authData });

                        if (authResult) {
                            redirectTo = authResult?.appState?.redirectTo;
                            const expiresInSec = _get(authResult, 'expiresIn', 60 * 60 * 24);
                            await AuthenticationHttpService.loginV2({
                                token: authResult?.accessToken,
                                expiresInSec,
                                employerId: authData?.employerId,
                            });
                            token = authResult.accessToken;
                            AuthenticationHttpService.setAuthCookie('ok', expiresInSec);
                        }
                    } catch (e) {
                        setAuthorizationProcessState(false);
                        await logout();
                        return;
                    }
                }

                setAuthToken(token);

                if (!authorizedUser) {
                    const {
                        userData,
                        userPrefs,
                        origUserPrefs,
                    } = await AuthenticationHttpService.authorize({
                        token,
                        userType: REGULAR,
                        connectionType: 'sso',
                        ssoId: 'paycor', // TODO move it as parameter
                    });

                    if (!userData) {
                        setAuthorizationProcessState(false);
                        await logout();
                        return;
                    }

                    if (redirectTo) {
                        userData.redirectTo = redirectTo;
                    }

                    const isLoggedInViaSSO = !isEmpty(userData?.sub);
                    const isSSOLoginPage = !!matchPath(
                        location.pathname,
                        '/loginsso*',
                    );

                    if (isSSOLoginPage && !isLoggedInViaSSO) {
                        const query = new URLSearchParams(location.search);

                        const redirectUri = TextService.sanitizeUrl(query.get('redirectUrl') || '');

                        if (redirectUri) {
                            window.location.href = redirectUri;
                        } else {
                            await logout({ goToLoginPage: false, reloadPage: true });
                        }

                        return;
                    }

                    const employerRepId = _get(origUserPrefs, 'employerRepId');

                    fetchSystemConfigAndPreferences();

                    let hasEmployerRepAdminRole = false;
                    let employer = null;

                    if (employerRepId) {
                        try {
                            let employerRepData = await EmployerRepHttpService.getEmployerRepDetails(employerRepId);

                            if (!employerRepData || employerRepData?.status?.current === EMPLOYER_REP_STATUSES.INACTIVE) {
                                setAuthorizationProcessState(false);
                                setEmployerRep(employerRepData);
                                const availableEmployers = await EmployerHttpService.getEmployersAvailableForEmployerRep();
                                setAvailableEmployerIds(availableEmployers);
                                AuthenticationHttpService.removeCookie('authData');
                                await AuthenticationHttpService.logout();
                                history.push(`/inactive-user-page?url=${(redirectTo || window.location.href).split('?').shift()}`);
                                return;
                            }

                            employerRepData = await EmployerRepHttpService.getEmployerRep(employerRepId);

                            setEmployerRep(employerRepData);
                            userData.user_name = _get(employerRepData, 'name');
                            hasEmployerRepAdminRole = _get(
                                employerRepData,
                                'roles',
                                '',
                            ).includes(EMPLOYER_RER_ADMIN_ROLE);

                            setAuthorizedUser({
                                user: userData,
                                userPrefs,
                                origUserPrefs: {
                                    ...origUserPrefs,
                                    ...(employer?.settings?.isResellerPartner
                                        && { resellerEmployerId: employer?._id }),
                                    ...(!employer?.settings?.isResellerPartner && employer?.resellerEmployerId
                                        && { isAssociatedEmployer: true }),
                                },
                                hasEmployerRepAdminRole,
                            });

                            setUserCookies(origUserPrefs, userPrefs, userData);

                            const availableEmployers = await EmployerHttpService.getEmployersAvailableForEmployerRep();
                            setAvailableEmployerIds(availableEmployers);
                            if (!includes(availableEmployers, authData?.employerId)) {
                                AuthenticationHttpService.removeCookie('authData');
                                history.push('/no-access-page');
                                setAuthorizationProcessState(false);
                                return;
                            }

                            employer = await EmployerHttpService.getEmployer(employerRepData?.employerId);
                        } catch (e) {
                            Logger.error(e);
                        }
                    }

                    if (!employerRepId) {
                        AuthenticationHttpService.removeCookie('authData');
                        history.push('/no-access-page');
                    }

                    setAuthorizationProcessState(false);
                }
            } catch (err) {
                Logger.error(err);
                setAuthorizationProcessState(false);
            }
        },
        [
            fetchSystemConfigAndPreferences,
            setAuthorizedUser,
            authorizedUser,
        ],
    );

    const tryToAuthorize = useCallback(async () => {
        setAuthorizationProcessState(true);

        try {
            const token = await AuthenticationHttpService.getIdToken();

            const query = new URLSearchParams(location.search);
            const redirectUrl = TextService.sanitizeUrl(query.get('redirectUrl') || '');

            if (!token) {
                const goToUrl = includes(['', '/', '/login'], window.location.pathname) ? '' : redirectUrl || window.location.href;
                await logout({ goToUrl });
                return;
            }

            setAuthToken(token);
            const {
                userData,
                userPrefs,
                origUserPrefs,
            } = await AuthenticationHttpService.authorize({
                token,
                userType: REGULAR,
            });

            if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
            }

            fetchSystemConfigAndPreferences();

            const employerRepId = _get(origUserPrefs, 'employerRepId');
            let hasEmployerRepAdminRole = false;
            let employer = null;

            if (employerRepId) {
                let employerRepData = await EmployerRepHttpService.getEmployerRepDetails(employerRepId);

                if (!employerRepData || employerRepData?.status?.current === EMPLOYER_REP_STATUSES.INACTIVE) {
                    setAuthorizationProcessState(false);
                    setEmployerRep(employerRepData);
                    const availableEmployers = await EmployerHttpService.getEmployersAvailableForEmployerRep();
                    setAvailableEmployerIds(availableEmployers);
                    AuthenticationHttpService.removeCookie('authData');
                    await AuthenticationHttpService.logout();
                    history.push(`/inactive-user-page?url=${window.location.href.split('?').shift()}`);
                    return;
                }

                employerRepData = await EmployerRepHttpService.getEmployerRep(
                    employerRepId,
                );

                employer = employerRepData?.employerId
                    ? await EmployerHttpService.getEmployer(employerRepData?.employerId)
                    : null;

                setEmployerRep(employerRepData);
                userData.user_name = _get(employerRepData, 'name');
                hasEmployerRepAdminRole = _get(employerRepData, 'roles', '')
                    .includes(EMPLOYER_RER_ADMIN_ROLE);
            }

            setAuthorizedUser({
                user: userData,
                userPrefs,
                origUserPrefs: {
                    ...origUserPrefs,
                    ...(employer?.settings?.isResellerPartner && { resellerEmployerId: employer?._id }),
                    ...(!employer?.settings?.isResellerPartner && employer?.resellerEmployerId
                        && { isAssociatedEmployer: true }),
                },
                hasEmployerRepAdminRole,
            });
            setUserCookies(origUserPrefs, userPrefs, userData);

            const employerIdFromRoute = ImpersonationService.getEmployerIdFromRoute(location);
            if (employerIdFromRoute && employerRepId) {
                const availableEmployers = await EmployerHttpService.getEmployersAvailableForEmployerRep();
                setAvailableEmployerIds(availableEmployers);
                if (availableEmployers && !isTalenyaUser && !isTalentExpertUser && !includes(availableEmployers, employerIdFromRoute)) {
                    AuthenticationHttpService.removeCookie('authData');
                    history.push('/no-access-page');
                    setAuthorizationProcessState(false);
                    return;
                }
            }
            AuthenticationHttpService.removeCookie('authData');
        } catch (err) {
            Logger.error(err.message);
        } finally {
            setAuthorizationProcessState(false);
        }
    }, [
        fetchSystemConfigAndPreferences,
        setAuthorizedUser,
    ]);

    const setUserCookies = (origUserPrefs, userPrefs, userData) => {
        AuthenticationHttpService.setCookie('origUserPrefs', JSON.stringify(origUserPrefs));
        AuthenticationHttpService.setCookie('userprefs', JSON.stringify(userPrefs));
        AuthenticationHttpService.setCookie('userData', JSON.stringify(userData));
    };

    const logout = useCallback(async ({
        goToLoginPage = true, reloadPage = false, goToUrl, reset = false,
    } = {}) => {
        let user = authorizedUser;
        let error = null;

        setAuthorizationProcessState(true);

        let regularLogout = true;
        if (!isTalenyaUser && !isTalentExpertUser) {
            let authData = UtilsService.parseJSON(decodeURIComponent(AuthenticationHttpService.getCookie('authData')));
            if (!authData?.employerId) {
                let positionId = ImpersonationService.getPositionIdFromRoute(location);
                let employerId = ImpersonationService.getEmployerIdFromRoute(location);
                if (!employerId && !positionId) {
                    const query = new URLSearchParams(location.search);
                    const redirectUri = query.get('redirectUrl');
                    if (redirectUri) {
                        const redirectUrlParsed = new URL(decodeURIComponent(redirectUri));
                        const redirectPathName = redirectUrlParsed.pathname;
                        employerId = ImpersonationService.getEmployerIdFromRoute({ pathname: redirectPathName });
                        positionId = ImpersonationService.getPositionIdFromRoute({ pathname: redirectPathName });
                    }
                }
                if (employerId || positionId) {
                    authData = await AuthenticationHttpService.getAuthData({
                        positionId,
                        employerId,
                    }).then((response) => {
                        const authDataWithEmployerId = {
                            ...response?.authData,
                            employerId: response?.employerId,
                        };
                        return authDataWithEmployerId;
                    });
                }
            }
            if (authData?.employerId && authData?.authType === EmployerRepAuthTypes.SSO) {
                const origin = window?.location?.origin;
                const redirectUrl = goToUrl || (reset ? `${origin}/login` : `${origin}/employer/${authData?.employerId}/positions`);
                const returnTo = `${origin}/loginsso?redirectUrl=${redirectUrl}`;

                const userData = UtilsService.parseJSON(decodeURIComponent(AuthenticationHttpService.getCookie('userData')));
                // simple pap logout for us tenant or sso (open id) complex federated logout
                const isSimpleLogout = !userData?.sub || userData?.sub.indexOf('auth0') === 0;
                if (isSimpleLogout) {
                    await AuthenticationHttpService.logout();
                    AuthenticationHttpService.getAuth0(authData).logout({ returnTo });
                } else {
                    await loadInBackground(
                        AuthenticationHttpService.getAuth0(authData).client.buildLogoutUrl({ federated: true, returnTo }),
                        async () => {
                            await AuthenticationHttpService.logout();
                            AuthenticationHttpService.getAuth0(authData).logout({ returnTo });
                        },
                    );
                }

                regularLogout = false;
            }
        }
        if (regularLogout) {
            try {
                await AuthenticationHttpService.logout();
                setUser(null);

                user = null;

                if (goToLoginPage && window.location.pathname !== '/login') {
                    if (isOnlyWebAppLoginAllowed) {
                        const redirectUrl = goToUrl ? `?redirectUrl=${goToUrl}` : '';
                        window.location.href = `${window.location.origin}/login${redirectUrl}`;
                    } else {
                        window.location.href = `${settings.TFTAppUrl}/#/login/dropAuth`;
                    }
                } else if (reloadPage) {
                    window.location.reload();
                }
            } catch (err) {
                Logger.error(err.message);
                error = err;
            } finally {
                setAuthorizationProcessState(false);
            }
        }

        return [user, error];
    }, [
        authorizedUser,
        setAuthorizedUser,
        isTalenyaUser,
        isTalentExpertUser,
    ]);

    const loginSso = (emplRep, redirectUrl) => {
        // eslint-disable-next-line max-len
        const extraParams = `&needLogout=true&loginHint=${emplRep?.email}&connection=Username-Password-Authentication&employerId=${emplRep?.employerId}`;
        const url = `${window.location.origin}/loginsso?redirectUrl=${redirectUrl}${extraParams}`;
        window.location.href = url;
    };

    const getAuthEntityType = useCallback(() => {
        const entityId = _get(
            authorizedUser,
            'origUserPrefs.data.entityId',
            null,
        );
        const employerRepId = _get(
            authorizedUser,
            'origUserPrefs.employerRepId',
            null,
        );

        if (entityId) {
            return AuthEntityTypes.RECRUITER;
        }

        if (employerRepId) {
            return AuthEntityTypes.EMPLOYER_REP;
        }
        return undefined;
    }, [authorizedUser]);

    const getAuthEntityId = useCallback(() => {
        const entityId = _get(
            authorizedUser,
            'origUserPrefs.data.entityId',
            null,
        );
        const employerRepId = _get(
            authorizedUser,
            'origUserPrefs.employerRepId',
            null,
        );

        return entityId || employerRepId;
    }, [authorizedUser]);

    const updateUserData = useCallback(({ updates }) => {
        setUser((currentState) => {
            const userDataClone = cloneDeep(currentState);

            for (const key in updates) {
                if (Object.prototype.hasOwnProperty.call(updates, key)) {
                    set(userDataClone, key, updates[key]);
                }
            }

            return userDataClone;
        });
    }, []);

    const syncUserLinkedinConnections = useCallback(() => {
        clearTimeout(syncLinkedinConnectionsTimer);
        const userId = authorizedUser?.origUserPrefs?._id;
        if (userId && authToken && window?.ChromeExtensionId) {
            ChromeExtensionService.syncUserLinkedinConnections({
                userId,
                authToken,
            });
        }
        syncLinkedinConnectionsTimer = setTimeout(syncUserLinkedinConnections, syncLinkedinConnectionsTimeout);
    }, [
        authorizedUser?.origUserPrefs?._id,
        authToken,
        window?.ChromeExtensionId,
    ]);

    const updateUserLinkedinId = useCallback(() => {
        if (window?.ChromeExtensionId) {
            ChromeExtensionService.getCurrentUserId()
                .then((id) => {
                    setUserLinkedinId(id);
                })
                .catch(() => {});
        }
    }, [window?.ChromeExtensionId]);

    useEffect(() => {
        checkIfThirdPartyCookieEnabled();

        if (!isPublicMode) {
            try {
                const asyncCall = async () => {
                    const isLoginPage = !!matchPath(
                        location.pathname,
                        '/login',
                    );

                    const isMainPage = includes(['', '/'], location.pathname);

                    if (isMainPage || isLoginPage) {
                        const token = await AuthenticationHttpService.getIdToken();
                        const userDataCookie = AuthenticationHttpService.getCookie('userData');
                        const authDataCookie = AuthenticationHttpService.getCookie('authData');
                        if (isLoginPage || (!userDataCookie && (authDataCookie || token))) {
                            await logout({ goToLoginPage: true, reset: true });
                            return;
                        }
                    }

                    const isSSOLoginExpiredPage = location.pathname === '/sso-login-expired-page';
                    const isNoAccessPage = location.pathname.indexOf('no-access-page') >= 0;
                    const isInactiveUserPage = location.pathname.indexOf('inactive-user-page') >= 0;
                    const isLogoutPage = location.pathname === '/logout';

                    if (isSSOLoginExpiredPage || isNoAccessPage || isInactiveUserPage || isLogoutPage) {
                        const availableEmployers = await EmployerHttpService.getEmployersAvailableForEmployerRep();
                        setAvailableEmployerIds(availableEmployers);
                        setAuthorizationProcessState(false);
                        return;
                    }

                    const query = new URLSearchParams(location.search);
                    let positionId = ImpersonationService.getPositionIdFromRoute(location);
                    let employerId = ImpersonationService.getEmployerIdFromRoute(location);
                    const requiresActivation = query.get('requiresActivation');
                    const employerRepId = query.get('employerRepId');
                    const invitationId = query.get('invitationId');
                    let redirectUri = query.get('redirectUrl');
                    AuthenticationHttpService.removeCookie('activationSuccessUrl');
                    let connection = '';
                    if (employerRepId) {
                        const employerRepDetails = invitationId
                            ? (await EmployerRepHttpService.getEmployerRepByInvitationId(invitationId)
                                || await EmployerRepHttpService.getEmployerRepDetails(employerRepId))
                            : await EmployerRepHttpService.getEmployerRepDetails(employerRepId);
                        const newMode = isOnlyWebAppLoginAllowed;
                        const activationUrl = EmployerRepService.getSignUpLink({ employerRepId, invitationId, newMode });
                        if (!employerRepDetails) {
                            window.location = activationUrl;
                            return;
                        }
                        if (employerRepDetails?.authType === EmployerRepAuthTypes.PAP) {
                            connection = 'Username-Password-Authentication';
                        }
                        if (requiresActivation && employerRepDetails?.status?.current === EMPLOYER_REP_STATUSES.INVITED) {
                            AuthenticationHttpService.setCookie('activationSuccessUrl', redirectUri, ssoLoginSessionExpirationTimeSec);
                            window.location = activationUrl;
                            return;
                        }
                    }

                    if (redirectUri) {
                        const redirectUrlParsed = new URL(decodeURIComponent(redirectUri));
                        const redirectPathName = redirectUrlParsed.pathname;
                        positionId = ImpersonationService.getPositionIdFromRoute({
                            pathname: redirectPathName,
                        });
                        employerId = ImpersonationService.getEmployerIdFromRoute({
                            pathname: redirectPathName,
                        });
                        if (!employerId) {
                            employerId = query.get('employerId');
                        }
                        if (!employerId && (location.hash || redirectUrlParsed.hash)) {
                            employerId = ImpersonationService.getEmployerIdFromRoute({
                                pathname: (location.hash || redirectUrlParsed.hash).replace('#', ''),
                            });
                            if (employerId) {
                                redirectUri = `${redirectUri.split('#').shift()}${(location.hash || redirectUrlParsed.hash).split('?').shift()}`;
                            }
                        }
                    }

                    const needLogout = query.get('needLogout');
                    const loginHint = query.get('loginHint');
                    connection = query.get('connection') || connection;
                    let authData = UtilsService.parseJSON(decodeURIComponent(AuthenticationHttpService.getCookie('authData')));

                    if (!authData?.employerId) {
                        authData = await AuthenticationHttpService.getAuthData({
                            positionId,
                            employerId,
                        }).then((response) => {
                            employerId = response?.employerId;

                            const authDataWithEmployerId = {
                                ...response?.authData,
                                employerId: response?.employerId,
                            };

                            AuthenticationHttpService.setCookie('authData', JSON.stringify(authDataWithEmployerId), ssoLoginSessionExpirationTimeSec);

                            return authDataWithEmployerId;
                        });
                    }

                    if (needLogout) {
                        if (!isEmpty(authData)) {
                            query.delete('needLogout');
                            let updatedQueryStr = query.toString();
                            updatedQueryStr = updatedQueryStr?.length ? `?${updatedQueryStr}` : '';
                            const origin = query.get('redirectUrl') || window.location.href.split('?').shift();
                            const updatedUrl = `${origin}${updatedQueryStr}`;

                            await logout({
                                goToLoginPage: false,
                                reloadPage: false,
                                goToUrl: updatedUrl,
                            });
                            return;
                        }
                    }

                    const isSetPositionOrEmployer = positionId || employerId;

                    const isSSOLoginPage = !!matchPath(
                        location.pathname,
                        '/loginsso*',
                    );

                    const token = await AuthenticationHttpService.getIdToken();

                    if (!authData?.employerId && isSSOLoginPage && !token && !isSetPositionOrEmployer) {
                        AuthenticationHttpService.removeCookie('authData');
                        if (redirectUri && redirectUri.indexOf('inactive-user-page') >= 0) {
                            history.push('/inactive-user-page');
                        } else if (redirectUri && redirectUri.indexOf('no-access-page') >= 0) {
                            history.push('/no-access-page');
                        } else if (redirectUri === `${window.location.origin}/login`) {
                            window.location.href = redirectUri;
                        } else {
                            history.push('/sso-login-expired-page');
                        }
                        setAuthorizationProcessState(false);
                        return;
                    }

                    const isSSOToken = authorizedUser && authorizedUser?.userData?.sub;
                    const useSSOFlow = !token || isSSOToken;

                    if (authData?.authType === EmployerRepAuthTypes.SSO && useSSOFlow) {
                        authData.data.redirectTo = redirectUri || window.location.href;
                        authData.data.redirectUri = `${window.location.origin}/loginsso`;
                        if (loginHint) {
                            authData.data.loginHint = loginHint;
                        }
                        if (connection || !includes(authData?.extraAuthTypes, EmployerRepAuthTypes.PAP)) {
                            authData.data.connection = connection || authData.data.connection;
                        } else {
                            delete authData.data.connection;
                        }
                        tryToAuthorizeSSO({ authData });
                    } else {
                        tryToAuthorize();
                    }
                };

                asyncCall().catch((error) => {
                    Logger.error(error.message);
                    tryToAuthorize();
                });
            } catch (e) {
                AuthenticationHttpService.removeCookie('authData');
                Logger.error(e.message);
                tryToAuthorize();
            }
        }
    }, []);

    useEffect(() => {
        syncUserLinkedinConnections();
    }, [
        authorizedUser?.origUserPrefs?._id,
        authToken,
        window?.ChromeExtensionId,
    ]);

    useEffect(() => {
        updateImpersonationInfo();
    }, [location.pathname]);

    useEffect(() => {
        updateUserLinkedinId();
    }, [window?.ChromeExtensionId]);

    const isUserDataFetched = authorizedUser !== null;

    const contextValue = useMemo(() => ({
        authToken,
        tryToAuthorize,
        tryToAuthorizeSSO,
        employerRep,
        user: authorizedUser,
        userLinkedinId,
        isTalenyaUser,
        isTalentExpertUser,
        isSuperUser: isTalenyaUser || isTalentExpertUser,
        isEmployerRepAdmin,
        isEmployerUser,
        logout,
        getAuthEntityType,
        getAuthEntityId,
        updateUserData,
        updateUserLinkedinId,
        syncUserLinkedinConnections,
        isUserDataFetched,
        loginSso,
        availableEmployerIds,
        isUserAllowedToEditSystemCampaigns,
    }), [
        authToken,
        tryToAuthorize,
        tryToAuthorizeSSO,
        employerRep,
        authorizedUser,
        userLinkedinId,
        isTalenyaUser,
        isTalentExpertUser,
        isEmployerRepAdmin,
        isEmployerUser,
        logout,
        getAuthEntityType,
        getAuthEntityId,
        updateUserData,
        updateUserLinkedinId,
        syncUserLinkedinConnections,
        isUserDataFetched,
        loginSso,
        availableEmployerIds,
        isUserAllowedToEditSystemCampaigns,
    ]);

    return authorizationProcess ? null : (
        <AuthorizationProvider value={contextValue}>
            {children}
        </AuthorizationProvider>
    );
};

AuthorizationComponent.propTypes = {
    children: PropTypes.node,
    location: PropTypes.object,
};

export const AuthorizedApp = withRouter(AuthorizationComponent);

export const useAuthContext = () => useContext(AuthorizationContext);

export default AuthorizationContext;
